import { useCallback } from 'react';

import { client } from '../../api/rest/client';
import { Actions } from '../../constants/actionTypes';
import {
  toastErrorTranslated,
  toastSuccessTranslated,
} from '../../helpers/toast';
import type { TGDPRUser } from '../../interfaces/TGDPR';
import { useMainDispatch } from '../useMainDispatch';

export const useGDPRActions = () => {
  const dispatch = useMainDispatch();

  const fetchGDPRUsers = useCallback(
    async (userFilter: string, page: number, perPage: number) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        const {
          data: { results, totalRecords },
        } = await client.gdpr.getGDPRUsers({
          query: {
            filter: userFilter,
            page: page.toString(),
            perPage: perPage.toString(),
          },
        });

        dispatch({
          type: Actions.SET_GDPR_USERS,
          payload: {
            gdprUsers: results || [],
            totalRecords: totalRecords || 0,
          },
        });
      } catch (error) {
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch],
  );

  const sendGDPR = useCallback(
    async (id: string, email?: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await client.gdpr.sendGDPREmailToUser({ body: { id, email } });
        toastSuccessTranslated('gdpr.send.successful');
      } catch (error: any) {
        const statusCode = error?.response?.status;

        if (statusCode === 400) {
          return false;
        }
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }

      return true;
    },
    [dispatch],
  );

  const deleteGDPR = useCallback(
    async (id: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await client.gdpr.deleteGDPRUser({ params: { id } });
        dispatch({ type: Actions.REMOVE_GDPR_USER, payload: id });
        toastSuccessTranslated('gdpr.delete.success');
      } catch (error) {
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch],
  );

  const setSelectedGDPRUser = useCallback(
    (gdprUser: TGDPRUser) => {
      dispatch({ type: Actions.SET_GDPR_USER, payload: gdprUser });
    },
    [dispatch],
  );

  return { fetchGDPRUsers, deleteGDPR, sendGDPR, setSelectedGDPRUser };
};
